import React, { FunctionComponent, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { HomeValueContext } from './context';
import { useFormContext, useStepper } from '../../../../hooks';
import { mainMediaMobile, utilNumberToCurrency } from '../../../../utils';
import HomeValueIconHuispedia from './icons/home-value-icon-huispedia';
import { Button, Heading } from '@chakra-ui/react';
import {
  CalculatorsHomeValueFormFields,
  CalculatorsHomeValueResult
} from '../../../../@types/features/calculators';
import { StepperPrevious } from '../../../shared/general';
import useMediaQuery from '../../../../hooks/use-media';

export const CalculatorHomeValueResult: FunctionComponent = () => {
  const intl = useIntl();
  const { propertyValue, selectedResidenceTypeLabel } = useContext(HomeValueContext);
  const { form, updateResult } = useFormContext<
    CalculatorsHomeValueResult,
    CalculatorsHomeValueFormFields
  >();
  const { steps, currentStep, setCurrentStep, setStepActive } = useStepper();
  const {
    predictedValue,
    city,
    postcode,
    street,
    houseNumber,
    houseNumberFull,
    buildYear,
    useSurface,
    volume,
    houseSubType,
    houseType,
    plotSize
  } = propertyValue;
  const propertyIsSelling = form.propertyPlans === 'sell';
  const isMobile = useMediaQuery(mainMediaMobile);
  const handleBackClick = () => {
    if (currentStep && currentStep !== 0) {
      const id = currentStep - 1;
      setStepActive(currentStep, true);
      setCurrentStep(id);
      updateResult(null);
    }
  };
  return (
    <div>
      <div className={`md:flex md:justify-between`}>
        <h2 className={`mb-8`}>
          <FormattedMessage id={`result.header`} defaultMessage={'Waarde van deze woning'} />
        </h2>
        {isMobile && (
          <StepperPrevious
            currentStep={currentStep}
            variant={`horizontal`}
            totalSteps={steps.length}
            title={intl.formatMessage({ id: 'result.back', defaultMessage: 'Vorige stap' })}
            handleBackClick={handleBackClick}
          />
        )}
        {!isMobile && (
          <StepperPrevious
            variant={`horizontal`}
            title={intl.formatMessage({ id: 'result.back', defaultMessage: 'Vorige stap' })}
            handleBackClick={handleBackClick}
          />
        )}
      </div>
      <p className={`mt-4`}>
        <FormattedMessage
          id={`result.text`}
          defaultMessage={`Het model is op grond van de ingevoerde gegevens tot deze waardering van de woning gekomen. De Woningwaardemeter is een schatting, géén taxatie.`}
        />
      </p>
      <div className={`rounded bg-white p-10 my-8`}>
        <div className={`mb-10`}>
          <p className={`text-gray-400`}>
            <FormattedMessage
              id={`result.predictedValue`}
              defaultMessage={'Geschatte woningwaarde'}
            />
          </p>
          <div className={`flex justify-between item-center`}>
            <h1 className={`mb-0`}>{utilNumberToCurrency(predictedValue, true, 0)}</h1>
            <HomeValueIconHuispedia />
          </div>
        </div>
        <div className={`h-[21rem] relative -mx-10`}>
          <img
            className={`object-cover w-full h-full`}
            src={`https://maps.googleapis.com/maps/api/streetview?size=800x350&key=AIzaSyDHMbJ5JBYgOnXI8ukdzxK5gIlPKHUK_ds&language=nl&region=NL&location=${street} ${houseNumber}, ${city}&sensor=false`}
            alt={`${postcode}, ${city}`}
          />

          <div className={`absolute left-5 bottom-5 text-white`}>
            <div>
              {street} {houseNumberFull}
            </div>
            <div>
              {postcode} {city}
            </div>
          </div>
        </div>

        <div className={`mt-8`}>
          <Heading size={`lg`}>
            {propertyIsSelling ? (
              <FormattedMessage
                id={`result.appointment.possibilities.title`}
                defaultMessage={`Meer weten over jouw hypotheekmogelijkheden`}
              />
            ) : (
              <FormattedMessage
                id={`result.appointment.continue.title`}
                defaultMessage={`Verder rekenen`}
              />
            )}
          </Heading>

          <Button
            colorScheme={`orange`}
            as={`a`}
            href={`${
              propertyIsSelling
                ? 'https://www.hypotheker.nl/afspraak-maken-hypotheekadviseur/'
                : 'https://www.hypotheker.nl/zelf-berekenen/hoeveel-kan-ik-lenen/'
            }`}>
            {propertyIsSelling ? (
              <FormattedMessage
                id={`result.appointment.possibilities.label`}
                defaultMessage={`Neem contact op`}
              />
            ) : (
              <FormattedMessage
                id={`result.appointment.continue.label`}
                defaultMessage={`Kan ik dit ook betalen`}
              />
            )}
          </Button>
        </div>

        <hr className={`my-8 border-gray-50`} />
        <Heading size={`lg`}>
          <FormattedMessage id={`result.residence.title`} defaultMessage={`Gegevens woning`} />
        </Heading>

        <table className={`w-1/2`}>
          <tbody>
            <tr>
              <td>
                <FormattedMessage id={`result.residence.type`} defaultMessage={`Soort woning`} />
              </td>
              <td>{selectedResidenceTypeLabel}</td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id={`result.residence.buildYear`} defaultMessage={`Bouwjaar`} />
              </td>
              <td>{buildYear}</td>
            </tr>
            <tr>
              <td>
                <FormattedMessage
                  id={`result.residence.totalSurface`}
                  defaultMessage={`Totaal woonoppervlak`}
                />
              </td>
              <td>
                {useSurface} m<sup>2</sup>
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage
                  id={`result.residence.volume`}
                  defaultMessage={`Inhoud van de woning`}
                />
              </td>
              <td>
                {volume} m<sup>3</sup>
              </td>
            </tr>
            {houseSubType !== 'Appartement' && houseType !== 'Appartement' && (
              <tr>
                <td>
                  <FormattedMessage
                    id={`result.residence.plotSize`}
                    defaultMessage={`Perceeloppervlak`}
                  />
                </td>
                <td>
                  {plotSize}m<sup>2</sup>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className={`mt-8`}>
          <small className={`text-gray-400`}>
            <FormattedMessage
              id={`result.source`}
              defaultMessage={` Berekend in samenwerking met Huispedia.`}
            />
          </small>
        </div>
      </div>
    </div>
  );
};
